import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, Skel, Tween } from './parts';
import { ago, failed, int, Loaded, SyncRun } from './api';

export type Check = { at: number; ok: boolean; ms: number };

type Tick = { tone: 'good' | 'warn' | 'bad' | 'none'; label: string };
type Tile = {
  name: string;
  sub: string;
  status: string;
  tone: 'good' | 'warn' | 'bad';
  ticks: Tick[];
  stats: [string, React.ReactNode][];
};

const toneVar = { good: 'var(--oa-good)', warn: 'var(--oa-warn)', bad: 'var(--oa-bad)' };
const STALE_DAYS = 8;
const TRACK = 20;

const k = (n: number | null | undefined) => (n == null ? '–' : n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : int(n));
const clock = (t: number | string) => new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const runTick = (r: SyncRun): Tick => ({
  tone: r.status === 'success' ? 'good' : r.status === 'failed' ? 'bad' : r.status === 'running' ? 'warn' : 'none',
  label: `${clock(r.startedAt)} · ${r.status === 'dry_run' ? 'Dry run' : r.status[0].toUpperCase() + r.status.slice(1)}${r.upserted != null ? ` · ${int(r.upserted)} rows` : ''}${r.error ? ` · ${r.error.slice(0, 60)}` : ''}`,
});

/** Pads a track to TRACK ticks, oldest first, empty slots on the left. */
const pad = (ticks: Tick[]) => [...Array.from({ length: Math.max(0, TRACK - ticks.length) }, () => ({ tone: 'none' as const, label: 'No record' })), ...ticks.slice(-TRACK)];

const buildTiles = (d: Loaded, checks: Check[]): Tile[] => {
  const tiles: Tile[] = [];
  if (failed(d.sync)) {
    tiles.push({ name: 'Product feed', sub: 'Ralawise import', status: 'Unknown', tone: 'warn', ticks: pad([]), stats: [['Error', d.sync.error.slice(0, 40)]] });
  } else {
    const runs = d.sync.runs;
    const last = runs[0];
    const lastOk = runs.find((r) => r.status === 'success');
    const ageDays = lastOk ? (Date.now() - new Date(lastOk.finishedAt || lastOk.startedAt).getTime()) / 864e5 : Infinity;
    const tone: Tile['tone'] = !last ? 'warn' : last.status === 'failed' ? 'bad' : ageDays > STALE_DAYS ? 'warn' : 'good';
    tiles.push({
      name: 'Product feed',
      sub: lastOk ? `Ralawise · synced ${ago(lastOk.finishedAt || lastOk.startedAt)}` : 'Ralawise · never synced',
      status: !last ? 'No runs' : last.status === 'failed' ? 'Failed' : last.status === 'running' ? 'Running' : ageDays > STALE_DAYS ? 'Stale' : 'Up to date',
      tone,
      ticks: pad([...runs].reverse().map(runTick)),
      stats: [
        ['Rows', <Tween key="u" value={lastOk?.upserted ?? 0} format={(n) => k(Math.round(n))} />],
        ['Retired', k(lastOk?.retired)],
        ['Live styles', <Tween key="s" value={d.sync.catalogue.liveStyles} />],
      ],
    });
  }
  const checkTile = (name: string, sub: string, list: Check[]): Tile => {
    const lastC = list[list.length - 1];
    const okN = list.filter((c) => c.ok).length;
    const slow = lastC && lastC.ok && lastC.ms > 1500;
    return {
      name,
      sub,
      status: !lastC ? 'Checking' : !lastC.ok ? 'Down' : slow ? 'Slow' : 'Operational',
      tone: !lastC ? 'warn' : !lastC.ok ? 'bad' : slow ? 'warn' : 'good',
      ticks: pad(list.map((c) => ({ tone: !c.ok ? 'bad' : c.ms > 1500 ? 'warn' : 'good', label: `${clock(c.at)} · ${c.ok ? `${int(c.ms)} ms` : 'No answer'}` }))),
      stats: [
        ['Up', list.length ? `${Math.round((okN / list.length) * 100)}%` : '–'],
        ['Latency', lastC ? <><Tween key="l" value={lastC.ms} duration={600} /> ms</> : '–'],
        ['Checks', String(list.length)],
      ],
    };
  };
  tiles.push(checkTile('Chat assistant', 'AI replies and handover', checks));
  return tiles;
};

const TileView: React.FC<{ t: Tile; i: number }> = ({ t, i }) => {
  const [pick, setPick] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 3200);
    return () => window.clearTimeout(id);
  }, []);
  const tick = pick !== null ? t.ticks[pick] : null;
  return (
    <li className="oa-tile" style={{ ['--i' as string]: i, ['--st' as string]: toneVar[t.tone] } as React.CSSProperties}>
      <div className="oa-r1">
        <span className="oa-pulse is-live" style={{ ['--i' as string]: i } as React.CSSProperties} aria-hidden="true" />
        <div className="min-w-0">
          <div className="oa-nm">{t.name}</div>
          <div className="oa-sb">{t.sub}</div>
        </div>
        <span className="oa-st">{t.status}</span>
      </div>
      <div className={cn('oa-trk', pick !== null && 'is-pick', ready && 'is-ready')} role="img" aria-label={`${t.name} history`} onMouseLeave={() => setPick(null)}>
        {t.ticks.map((x, j) => (
          <i key={j} className={cn(x.tone !== 'good' && `is-${x.tone}`, pick === j && 'is-on')} style={{ ['--j' as string]: j } as React.CSSProperties} onMouseEnter={() => setPick(j)} />
        ))}
      </div>
      <div className="oa-r3">
        {tick ? (
          <span className="truncate text-[var(--oa-text)]">{tick.label}</span>
        ) : (
          t.stats.map(([label, v]) => (
            <span key={label} className="whitespace-nowrap">
              {label} <b>{v}</b>
            </span>
          ))
        )}
      </div>
    </li>
  );
};

/** Product feed and chat assistant, stacked; each tile grows to fill the card. */
export const SystemsCard: React.FC<{ data: Loaded | null; checks: Check[] }> = ({ data, checks }) => {
  const tiles = data ? buildTiles(data, checks) : [];
  const healthy = tiles.filter((t) => t.tone === 'good').length;
  const lastCheck = checks[checks.length - 1];
  return (
    <Card
      i={4}
      area="sys"
      title="Sync & systems"
      actions={
        data ? (
          <span className={cn('oa-chip', healthy === tiles.length ? 'is-good' : healthy >= tiles.length - 1 ? 'is-warn' : 'is-bad')} title={`Checked ${lastCheck ? ago(new Date(lastCheck.at).toISOString()) : 'just now'} · refreshes every minute`}>
            {healthy === tiles.length ? 'All normal' : `${healthy} of ${tiles.length} healthy`}
          </span>
        ) : null
      }
    >
      <ul className="oa-tiles is-stack">
        {data
          ? tiles.map((t, i) => <TileView key={t.name} t={t} i={i} />)
          : [0, 1].map((i) => (
              <li key={i} className="list-none">
                <Skel className="h-full min-h-[104px] w-full" />
              </li>
            ))}
      </ul>
      <p className="text-[11.5px] text-[var(--oa-faint)]">Checked {lastCheck ? ago(new Date(lastCheck.at).toISOString()) : 'just now'} · refreshes every minute</p>
    </Card>
  );
};
